import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';

import { FIELD_PATHS, URL_PARAM_CUSTOMER_ID, VISIT_CONTACT_TYPE } from '@funnels/constants';
import { RelatedParty, StoreApplicant, UseParams } from '@funnels/types';

import { useCustomer } from '../useCustomer';
import { useFormValues } from '../useFormValues';
import { usePrevious } from '../usePrevious';

export function useUpdateRelatedParties(relatedParties: RelatedParty[]) {
  const [formValues, setFormValue] = useFormValues();
  // Get logged in applicant info, if customer id cannot be retrieved from customer context,
  // which means it is in team view, then get customer id through url parameters
  const customer = useCustomer();
  const params = useParams<UseParams>();
  const customerId = customer.data.id || params[URL_PARAM_CUSTOMER_ID];
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const applicant = applicants.find((applicant) => applicant.customerId === customerId);
  // Remember previous related parties for comparison
  const prevRelatedParties: RelatedParty[] | undefined = usePrevious(relatedParties);
  const relatedPartyIndicesToBeUpdated = relatedParties.reduce((indices, relatedParty, index) => {
    const contactType = get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0));
    const prevContactType = prevRelatedParties
      ? get({ relatedParties: [prevRelatedParties[index]] }, FIELD_PATHS.RELATED_PARTY_TYPE(0))
      : undefined;
    // check if current related party's contact type is Loan Party(applicant) and different from previous one
    if (contactType !== prevContactType) {
      indices.push(index);
    }
    return indices;
  }, [] as number[]);
  // Updating upon contact type change has 2 scenarios:
  // 1. If contact type is not REAL_ESTATE_AGENT, we set business name to empty
  // 2. If contact type is LOAN_PARTY, we use currently logged in applicant's info as contact info if contact is all empty
  // normally we would only update one related party as in theory user can only change one contact type at a time,
  // however, it is possible to have 2 or more properties which are refinance and owner occupied, in such cases,
  // we need to update more than one related parties with applicant info as contact at once,
  // so react final form will not update state separately
  useEffect(() => {
    relatedPartyIndicesToBeUpdated.forEach((relatedPartyIndex) => {
      const contactType = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_TYPE(relatedPartyIndex));
      const contactTitle = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_TITLE(relatedPartyIndex));
      const contactFirstName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_FIRST_NAME(relatedPartyIndex));
      const contactLastName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_LAST_NAME(relatedPartyIndex));
      const contactPhone = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_PHONE(relatedPartyIndex));
      const contactBusinessName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_BUSINESS_NAME(relatedPartyIndex));
      if (
        (contactType === VISIT_CONTACT_TYPE.LoanParty || contactType === VISIT_CONTACT_TYPE.Tenant) &&
        !!contactBusinessName
      ) {
        setFormValue(FIELD_PATHS.RELATED_PARTY_BUSINESS_NAME(relatedPartyIndex), '');
      }
      if (
        contactType === VISIT_CONTACT_TYPE.LoanParty &&
        !contactTitle &&
        !contactFirstName &&
        !contactLastName &&
        !contactPhone
      ) {
        setFormValue(
          FIELD_PATHS.RELATED_PARTY_TITLE(relatedPartyIndex),
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TITLE(0))
        );
        setFormValue(
          FIELD_PATHS.RELATED_PARTY_FIRST_NAME(relatedPartyIndex),
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_FIRST_NAME(0))
        );
        setFormValue(
          FIELD_PATHS.RELATED_PARTY_LAST_NAME(relatedPartyIndex),
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_LAST_NAME(0))
        );
        setFormValue(
          FIELD_PATHS.RELATED_PARTY_PHONE(relatedPartyIndex),
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_MOBILE(0))
        );
      }
    });
  }, [applicant, relatedParties, relatedPartyIndicesToBeUpdated, setFormValue]);
}
